/**
 * `useAnchoredPosition` — portal-safe placement for tooltip and popover
 * primitives. Caller passes a trigger ref and the floating element's ref;
 * the hook returns an absolute viewport position (document.body portal,
 * `position: fixed`) and a visibility flag.
 *
 * Behaviour (spec: labels-review/ux.md §12.3):
 *   - preferred side: top | bottom | left | right, with 6px offset.
 *   - **flip**: if the preferred side has less room than `floating + margin`,
 *     use the opposite side. If neither fits, pick the side with more room.
 *   - **shift**: clamp along the cross axis so the bubble stays ≥ 8px inside
 *     the viewport. Bubble may become off-centred relative to the trigger.
 *   - **clip to visible trigger**: if the trigger's bounding box is outside
 *     its nearest scroll container's visible rect, hide the floating element.
 *   - Recomputes on scroll (capture, so every ancestor fires), resize, and
 *     content-size change (ResizeObserver), coalesced to one per animation
 *     frame.
 *
 * Not a full floating-ui replacement. Small,, no dependency (CLAUDE.md §8).
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type AnchorSide = 'top' | 'bottom' | 'left' | 'right';

export interface AnchoredPosition {
  readonly top: number;
  readonly left: number;
  readonly side: AnchorSide;
  readonly visible: boolean;
}

export interface UseAnchoredPositionOptions {
  readonly open: boolean;
  readonly side?: AnchorSide;
  readonly offset?: number;
  readonly viewportMargin?: number;
  /** Cross-axis alignment before shifting. `center` (default) for tooltips;
   *  `start` / `end` align the floating edge with the trigger's edge (menus). */
  readonly align?: AnchorAlign;
}

export type AnchorAlign = 'center' | 'start' | 'end';

/** Every ancestor whose computed overflow clips its content (auto | scroll |
 *  overlay | hidden on either axis). `hidden` counts: it clips the trigger
 *  even though it does not scroll. Empty when nothing clips; the viewport is
 *  then the only visible rect. */
function clippingAncestors(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  let node: HTMLElement | null = el.parentElement;
  while (node !== null && node !== document.body && node !== document.documentElement) {
    const s = window.getComputedStyle(node);
    if (/(auto|scroll|overlay|hidden)/.test(`${s.overflow} ${s.overflowX} ${s.overflowY}`)) out.push(node);
    node = node.parentElement;
  }
  return out;
}

/** Pick a side that fits, flipping to opposite when the preferred side cannot
 *  hold the floating element. If neither side fits, pick the side with more
 *  room. */
export function pickSide(
  preferred: AnchorSide,
  trigger: DOMRect,
  floating: { w: number; h: number },
  margin: number,
  vw: number = typeof window === 'undefined' ? 0 : window.innerWidth,
  vh: number = typeof window === 'undefined' ? 0 : window.innerHeight,
): AnchorSide {
  const spaceTop = trigger.top - margin;
  const spaceBottom = vh - trigger.bottom - margin;
  const spaceLeft = trigger.left - margin;
  const spaceRight = vw - trigger.right - margin;
  const fits: Record<AnchorSide, boolean> = {
    top: spaceTop >= floating.h,
    bottom: spaceBottom >= floating.h,
    left: spaceLeft >= floating.w,
    right: spaceRight >= floating.w,
  };
  const opp: Record<AnchorSide, AnchorSide> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  if (fits[preferred]) return preferred;
  if (fits[opp[preferred]]) return opp[preferred];
  // Neither side fits — pick the side (preferred axis) with more room.
  const axis = preferred === 'top' || preferred === 'bottom'
    ? [spaceTop, spaceBottom] as const
    : [spaceLeft, spaceRight] as const;
  if (preferred === 'top' || preferred === 'bottom') {
    return axis[0] >= axis[1] ? 'top' : 'bottom';
  }
  return axis[0] >= axis[1] ? 'left' : 'right';
}

/** Compute position given a resolved side; `shift` keeps the floating box
 *  inside the viewport on the cross axis. */
export function placeOnSide(
  side: AnchorSide,
  trigger: DOMRect,
  floating: { w: number; h: number },
  offset: number,
  margin: number,
  vw: number,
  vh: number,
  align: AnchorAlign = 'center',
): { top: number; left: number } {
  let top = 0;
  let left = 0;
  if (side === 'top') {
    top = trigger.top - floating.h - offset;
    left = trigger.left + trigger.width / 2 - floating.w / 2;
  } else if (side === 'bottom') {
    top = trigger.bottom + offset;
    left = trigger.left + trigger.width / 2 - floating.w / 2;
  } else if (side === 'left') {
    top = trigger.top + trigger.height / 2 - floating.h / 2;
    left = trigger.left - floating.w - offset;
  } else {
    top = trigger.top + trigger.height / 2 - floating.h / 2;
    left = trigger.right + offset;
  }
  if (align !== 'center') {
    if (side === 'top' || side === 'bottom') {
      left = align === 'start' ? trigger.left : trigger.right - floating.w;
    } else {
      top = align === 'start' ? trigger.top : trigger.bottom - floating.h;
    }
  }
  // Shift along the cross axis so the bubble stays inside the viewport.
  if (side === 'top' || side === 'bottom') {
    if (left < margin) left = margin;
    if (left + floating.w > vw - margin) left = Math.max(margin, vw - margin - floating.w);
  } else {
    if (top < margin) top = margin;
    if (top + floating.h > vh - margin) top = Math.max(margin, vh - margin - floating.h);
  }
  return { top, left };
}

/** True when the trigger's centre point is inside the viewport and inside
 *  the visible rect of every clipping ancestor (spec §12.3 rule 5: a trigger
 *  scrolled out of its panel must not leave a bubble floating over unrelated
 *  content). */
export function isTriggerVisible(trigger: DOMRect, clips: readonly DOMRect[], vw: number, vh: number): boolean {
  // Zero-sized rect at the origin (jsdom, or before first layout) is not
  // evidence of being scrolled off.
  if (trigger.width === 0 && trigger.height === 0 && trigger.left === 0 && trigger.top === 0) return true;
  const cx = trigger.left + trigger.width / 2;
  const cy = trigger.top + trigger.height / 2;
  const inside = (r: { top: number; left: number; bottom: number; right: number }): boolean =>
    cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom;
  if (!inside({ top: 0, left: 0, right: vw, bottom: vh })) return false;
  return clips.every(inside);
}

export function useAnchoredPosition(
  triggerRef: React.RefObject<HTMLElement>,
  floatingRef: React.RefObject<HTMLElement>,
  options: UseAnchoredPositionOptions,
): AnchoredPosition {
  const { open, side: preferred = 'top', offset = 6, viewportMargin = 8, align = 'center' } = options;
  const [pos, setPos] = useState<AnchoredPosition>({ top: 0, left: 0, side: preferred, visible: false });
  const rafRef = useRef<number | null>(null);

  const compute = useCallback((): void => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (trigger === null || floating === null) return;
    const t = trigger.getBoundingClientRect();
    const clips = clippingAncestors(trigger).map((n) => n.getBoundingClientRect());
    const visible = isTriggerVisible(t, clips, window.innerWidth, window.innerHeight);
    if (!visible) {
      setPos((p) => (p.visible ? { ...p, visible: false } : p));
      return;
    }
    const w = floating.offsetWidth;
    const h = floating.offsetHeight;
    const resolved = pickSide(preferred, t, { w: w + offset, h: h + offset }, viewportMargin, window.innerWidth, window.innerHeight);
    const placed = placeOnSide(resolved, t, { w, h }, offset, viewportMargin, window.innerWidth, window.innerHeight, align);
    setPos({ top: placed.top, left: placed.left, side: resolved, visible: true });
  }, [triggerRef, floatingRef, preferred, offset, viewportMargin, align]);

  const schedule = useCallback((): void => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      compute();
    });
  }, [compute]);

  useLayoutEffect(() => {
    if (!open) {
      setPos((p) => (p.visible ? { ...p, visible: false } : p));
      return;
    }
    compute();
    // One more pass after mount so the floating element's measured size is
    // used (the first call reads the pre-paint size which may be zero).
    const r = window.requestAnimationFrame(compute);
    return () => window.cancelAnimationFrame(r);
  }, [open, compute]);

  useEffect(() => {
    if (!open) return;
    const onScroll = (): void => schedule();
    const onResize = (): void => schedule();
    // Capture phase so every ancestor scroll fires, not just window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // ResizeObserver on the floating element so content-size changes
    // (e.g. an async label replacing a loading dash) reposition correctly.
    let ro: ResizeObserver | null = null;
    const floating = floatingRef.current;
    if (floating !== null && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => schedule());
      ro.observe(floating);
      const trigger = triggerRef.current;
      if (trigger !== null) ro.observe(trigger);
    }
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [open, schedule, triggerRef, floatingRef]);

  return pos;
}
